import type { TFunction } from 'i18next';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { OperatorStatusPill } from '../../components/dashboard/OperatorStatusPill';
import { displayManagePath } from '../displays/manage/displayManageRoutes';
import type { HomeDisplayRow } from './buildHomeDisplayRows';

type HomeMobileDisplaysProps = {
  rows: HomeDisplayRow[];
};

function playingLabel(row: HomeDisplayRow, t: TFunction<'home'>): string {
  if (row.status === 'not-connected') return t('mobile.displays.notPaired');
  if (!row.playlistName) return t('mobile.displays.nothingPlaying');
  const playlist = row.playlistName;
  if (row.status === 'offline') return t('mobile.displays.lastPlayed', { playlist });
  if (row.status === 'syncing') return t('mobile.displays.syncing', { playlist });
  return t('mobile.displays.playing', { playlist });
}

const MOBILE_HOME_DISPLAY_LIMIT = 5;

export function HomeMobileDisplays({ rows }: HomeMobileDisplaysProps) {
  const { t } = useTranslation('home');
  const visible = rows.slice(0, MOBILE_HOME_DISPLAY_LIMIT);

  return (
    <section className="flex flex-col gap-2.5">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="m-0 text-[13px] font-bold uppercase tracking-[0.12em]" style={{ color: 'var(--gs-muted)' }}>
          {t('mobile.displays.title')}
        </h2>
        <Link
          to="/screens"
          className="text-[13px] font-semibold no-underline"
          style={{ color: 'var(--gs-accent)' }}
        >
          {t('mobile.displays.viewAll')}
        </Link>
      </div>

      {rows.length === 0 ? (
        <div
          className="rounded-[14px] border px-4 py-5 text-sm"
          style={{ background: 'var(--gs-panel)', borderColor: 'var(--gs-line)', color: 'var(--gs-muted)' }}
        >
          <Trans
            t={t}
            i18nKey="mobile.displays.empty"
            components={{
              screensLink: (
                <Link to="/screens#setup" className="font-semibold" style={{ color: 'var(--gs-accent)' }} />
              ),
            }}
          />
        </div>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
          {visible.map((row) => (
            <li key={row.id}>
              <article
                className="flex flex-col gap-3 rounded-[14px] border p-3.5"
                style={{ background: 'var(--gs-panel)', borderColor: 'var(--gs-line)' }}
              >
                <div className="flex items-start gap-3">
                  <span
                    className="grid h-10 w-10 shrink-0 place-items-center rounded-[11px]"
                    style={{ background: 'var(--gs-panel2)', color: 'var(--gs-muted)' }}
                    aria-hidden
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
                      <rect x="3" y="4" width="18" height="12" rx="2" />
                      <path d="M9 20h6M12 16v4" />
                    </svg>
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="m-0 truncate text-[15px] font-bold" style={{ color: 'var(--gs-ink)' }}>
                      {row.name}
                    </p>
                    <p className="m-0 mt-0.5 truncate text-[13px]" style={{ color: 'var(--gs-muted)' }}>
                      {playingLabel(row, t)}
                    </p>
                  </div>
                  <OperatorStatusPill status={row.status} />
                </div>
                <Link
                  to={displayManagePath(row.id)}
                  className="rounded-[10px] px-3 py-2.5 text-center text-sm font-bold no-underline"
                  style={{
                    color: 'var(--gs-accent-ink)',
                    background: 'var(--gs-accent-solid)',
                  }}
                >
                  {row.status === 'not-connected'
                    ? t('mobile.displays.connectTv')
                    : t('mobile.displays.manage')}
                </Link>
              </article>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
